import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { ShieldCheck, Camera, FileImage } from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { api } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { ImageUpload } from '@/components/ImageUpload';

export default function IdVerificationPage() {
  const { user, refreshMe } = useAuth();
  const nav = useNavigate();
  const [docType, setDocType] = useState('carte_electeur');
  const [front, setFront] = useState(null);
  const [back, setBack] = useState(null);
  const [selfie, setSelfie] = useState(null);
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState(null);

  useEffect(() => {
    if (user) { api.get('/users/me/verification-status').then((r) => setStatus(r.data.verification)); }
  }, [user?.id]);

  const submit = async () => {
    if (!front || !selfie) return toast.error('Photo de la pièce (recto) et selfie requis');
    setLoading(true);
    try {
      await api.post('/users/me/verify-id', { doc_type: docType, doc_front_base64: front, doc_back_base64: back, selfie_base64: selfie });
      toast.success("Vérification soumise. L'équipe Job Kongo traite sous 24-48h.");
      await refreshMe();
      nav('/dashboard');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur');
    } finally { setLoading(false); }
  };

  return (
    <div className="max-w-2xl mx-auto px-4 md:px-6 py-6 space-y-4" data-testid="verify-id-page">
      <div className="flex items-center gap-2">
        <ShieldCheck size={22} className="text-[#16764A]" />
        <h1 className="text-2xl font-bold">Vérification d&apos;identité</h1>
      </div>
      <Card className="p-4 bg-[#DFF7EA] border-[#1F9D63]/30">
        <p className="text-sm text-[#16764A]">Vos documents sont chiffrés et utilisés uniquement pour la vérification. Ils ne seront jamais partagés.</p>
      </Card>

      {status?.status === 'pending' && (
        <Card className="p-4 bg-[#FFF6C7] border-[#F7D618]/60">
          <p className="text-sm font-semibold text-[#7A5F00]">Vérification en cours de traitement.</p>
          <p className="text-xs text-[#5C4600] mt-1">Nous revenons vers vous sous 24-48h.</p>
        </Card>
      )}

      {(status?.status !== 'pending' || !status) && (
        <Card className="p-4 space-y-4">
          <div>
            <Label>Type de document</Label>
            <select className="mt-1 h-10 w-full rounded-md border border-input bg-white px-3 text-sm" value={docType} onChange={(e) => setDocType(e.target.value)} data-testid="verify-doc-type">
              <option value="carte_electeur">Carte d&apos;électeur</option>
              <option value="passeport">Passeport</option>
              <option value="permis">Permis de conduire</option>
            </select>
          </div>
          <div>
            <Label className="flex items-center gap-1"><FileImage size={14} /> Pièce — recto</Label>
            <ImageUpload value={front} onChange={setFront} label="Photo de la pièce (recto)" testid="verify-doc-front" />
          </div>
          <div>
            <Label className="flex items-center gap-1"><FileImage size={14} /> Pièce — verso (optionnel)</Label>
            <ImageUpload value={back} onChange={setBack} label="Photo de la pièce (verso)" testid="verify-doc-back" />
          </div>
          <div>
            <Label className="flex items-center gap-1"><Camera size={14} /> Selfie avec la pièce</Label>
            <ImageUpload value={selfie} onChange={setSelfie} label="Selfie tenant la pièce" testid="verify-selfie" />
          </div>
          <div className="text-xs text-slate-600 space-y-1">
            <p>✅ Photo nette, sans reflet</p>
            <p>✅ Document entier visible</p>
            <p>✅ Selfie bien éclairé, visage visible</p>
          </div>
          <Button onClick={submit} disabled={loading} className="w-full bg-[#1677F2] hover:bg-[#0F5FCC]" data-testid="verify-submit-btn">
            {loading ? 'Envoi…' : 'Soumettre la vérification'}
          </Button>
        </Card>
      )}
    </div>
  );
}
